import { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { v4 } from 'uuid'
import { SOCKET_URL } from '../env/env'
import { CHALLENGE_TYPES } from '../utils/constant'
import {
  createRunCodeTimer,
  getJWTToken,
  needEventTimeout,
  showRunCodeErrorForDisconnect
} from '../utils/utils'

export const SocketContext = createContext({
  socket: null,
  initSocket: () => null,
  closeSocket: () => null,
  isSocketConnected: false,
  IntialiseMCQ: () => null,
  IntialiseEditor: () => null,
  sendEditorRunCode: () => null,
  sendEditorFileOpen: () => null,
  sendEditorFileSave: () => null,
  SendSocketHeartBeat: () => null,
  sendSocketReconnect: () => null,
  sendEditorFileDelete: () => null,
  sendEditorFileChange: () => null,
  getMediaServerEndPoint: () => null,
  sendSubmitContestEditor: () => null,
  sendCloseOtherConnection: () => null,
  sendCandidateDisqualify: () => null,
  sendInstallDependency: () => null,
  sendEditorRestartApp: () => null,
  sendEditorCodeForIntellisense: () => null,
  handleSocketAuthorization: () => null,
  sendDBInit: () => null,
  sendDBLoad: () => null,
  sendRunSQLScript: () => null,
  sendSQLChange: () => null,
  sendRunAppSQL: () => null,
  sendResetAppDatabase: () => null,
  InitAIScreening: () => null,
  sendGetAllMessages: () => null,
  socketAuthenticated: false,
  setSocketAuthenticated: () => null
})

export const SocketProvider = ({ children }) => {
  // Core refs
  const socket = useRef(null)
  const socketInitConfig = useRef({ url: '', checkMeta: false })
  const isSocketClosedIntentionally = useRef(false)

  // Old timers
  const checkConnectionTimeoutRef = useRef(null)
  const checkMetadataRef = useRef(null)

  // New: single-shot reconnect with backoff
  const reconnectTimeoutRef = useRef(null)
  const backoffMsRef = useRef(1000) // 1s start, doubles up to max
  const backoffMaxMs = 15000

  // New: generation guard to ignore stale socket events
  const connectionIdRef = useRef(0)

  // New: ping sequence tracking to avoid crossing responses
  const pendingPingIdRef = useRef(null) // last sent check-connection id
  const lastAckedPingIdRef = useRef(null)

  // State
  const [isSocketConnected, setIsSocketConnected] = useState(false)
  const [socketAuthenticated, setSocketAuthenticated] = useState(false)

  // Helpers
  const clearCheckConnectionTimeoutRef = useCallback(() => {
    if (checkConnectionTimeoutRef.current) {
      clearTimeout(checkConnectionTimeoutRef.current)
      checkConnectionTimeoutRef.current = null
    }
  }, [])

  const clearCheckMetadataTimeout = useCallback(() => {
    if (checkMetadataRef.current) {
      clearTimeout(checkMetadataRef.current)
      checkMetadataRef.current = null
    }
  }, [])

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  const scheduleReconnect = useCallback((immediate = false) => {
    if (isSocketClosedIntentionally.current || reconnectTimeoutRef.current)
      return
    const delay = immediate ? 0 : backoffMsRef.current
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null
      backoffMsRef.current = Math.min(
        backoffMaxMs,
        Math.max(1000, backoffMsRef.current * 2)
      )
      initSocket(
        socketInitConfig.current.url,
        socketInitConfig.current.checkMeta
      )
    }, delay)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetBackoff = useCallback(() => {
    backoffMsRef.current = 1000
  }, [])

  const sendMessage = useCallback((method, parameters = {}) => {
    if (socket?.current?.readyState === WebSocket.OPEN) {
      const id = v4()
      const needTimer = needEventTimeout(method)

      if (needTimer) {
        createRunCodeTimer(id, needTimer?.key, needTimer?.message)
      }

      if (method === 'check-connection') {
        pendingPingIdRef.current = id
      }

      socket.current.send(JSON.stringify({ id, method, parameters }))
      return true
    }
    return false
  }, [])

  const closeSocket = useCallback(
    (isClosingIntentionally = true) => {
      setIsSocketConnected(false)
      setSocketAuthenticated(false)
      isSocketClosedIntentionally.current = isClosingIntentionally
      if (isClosingIntentionally) clearReconnectTimeout()
      clearCheckMetadataTimeout()
      clearCheckConnectionTimeoutRef()

      const ws = socket.current
      if (!ws) return

      if (isClosingIntentionally) {
        try {
          ws.onopen = null
          ws.onclose = null
          ws.onmessage = null
        } catch {}
      }

      try {
        ws.close(1000, 'Normal Closure')
      } catch {}
      socket.current = null
      if (!isClosingIntentionally) {
        setTimeout(() => {
          if (
            !reconnectTimeoutRef.current &&
            !isSocketClosedIntentionally.current
          ) {
            scheduleReconnect(true)
          }
        }, 300)
      }
    },
    [
      clearReconnectTimeout,
      clearCheckMetadataTimeout,
      clearCheckConnectionTimeoutRef,
      scheduleReconnect
    ]
  )

  const handleSendConnectionMessage = useCallback(
    (myGen) => {
      if (connectionIdRef.current !== myGen) return
      const sent = sendMessage('check-connection')
      if (!sent) return
      const expectedId = pendingPingIdRef.current
      clearCheckConnectionTimeoutRef()
      checkConnectionTimeoutRef.current = setTimeout(() => {
        console.log('Check Connection Timeout')
        if (
          lastAckedPingIdRef.current !== expectedId &&
          connectionIdRef.current === myGen
        ) {
          closeSocket(false)
          scheduleReconnect()
        }
      }, 5000)
    },
    [
      sendMessage,
      scheduleReconnect,
      closeSocket,
      clearCheckConnectionTimeoutRef
    ]
  )

  const initialiseCheckMetadataTimeout = useCallback(
    (myGen) => {
      clearCheckMetadataTimeout()
      checkMetadataRef.current = setTimeout(() => {
        console.log('Metadata Timeout')
        if (isSocketClosedIntentionally.current) {
          clearCheckMetadataTimeout()
        } else if (connectionIdRef.current === myGen) {
          closeSocket(false)
          scheduleReconnect()
        }
      }, 30000)
    },
    [clearCheckMetadataTimeout, closeSocket, scheduleReconnect]
  )

  const initSocket = useCallback(
    (socketUrl, checkMeta = false) => {
      socketInitConfig.current = { url: socketUrl, checkMeta }

      if (socket.current && socket.current.readyState === WebSocket.CLOSING) {
        setTimeout(() => scheduleReconnect(true), 200)
        return
      }

      if (!socket.current || socket.current.readyState === WebSocket.CLOSED) {
        const myGen = ++connectionIdRef.current
        resetBackoff()
        clearReconnectTimeout()

        const url = socketUrl || `${SOCKET_URL}ws/challenge/editor`
        const ws = new WebSocket(url)

        ws.onopen = () => {
          if (connectionIdRef.current !== myGen) return
          setIsSocketConnected(true)
          clearReconnectTimeout()
          clearCheckConnectionTimeoutRef()
          clearCheckMetadataTimeout()
          showRunCodeErrorForDisconnect()
          isSocketClosedIntentionally.current = false
          handleSendConnectionMessage(myGen)
          if (checkMeta) initialiseCheckMetadataTimeout(myGen)
        }

        ws.onmessage = (event) => {
          if (connectionIdRef.current !== myGen) return

          let data = null
          try {
            data = JSON.parse(event?.data || '{}')
          } catch {
            return
          }

          if (data?.method === 'check-connection-response') {
            if (data?.id && data.id === pendingPingIdRef.current) {
              lastAckedPingIdRef.current = data.id
            }
            clearCheckConnectionTimeoutRef()
            if (!isSocketClosedIntentionally.current) {
              setTimeout(() => handleSendConnectionMessage(myGen), 1000)
            }
            return
          }

          if (data?.method === 'media-server/worker-metadata' && checkMeta) {
            clearCheckMetadataTimeout()
            if (!isSocketClosedIntentionally.current) {
              setTimeout(() => initialiseCheckMetadataTimeout(myGen), 1000)
            }
            return
          }
        }

        ws.onclose = () => {
          if (connectionIdRef.current !== myGen) return
          setIsSocketConnected(false)
          setSocketAuthenticated(false)
          clearCheckMetadataTimeout()
          clearCheckConnectionTimeoutRef()
          if (!isSocketClosedIntentionally.current) {
            scheduleReconnect()
          }
        }

        socket.current = ws
      }
    },
    [
      scheduleReconnect,
      clearReconnectTimeout,
      clearCheckConnectionTimeoutRef,
      clearCheckMetadataTimeout,
      handleSendConnectionMessage,
      initialiseCheckMetadataTimeout,
      resetBackoff
    ]
  )

  const SendSocketHeartBeat = useCallback(() => {
    sendMessage('challenge-editor/heartbeat')
  }, [sendMessage])

  const handleSocketAuthorization = useCallback(() => {
    sendMessage('challenge-editor/authorization', { token: getJWTToken() })
  }, [sendMessage])

  const IntialiseEditor = useCallback(
    (id, attemptId, contestId) => {
      sendMessage('challenge-editor/load', {
        programmingChallengeId: id,
        ...(contestId ? { contestId } : {}),
        ...(attemptId ? { attemptId } : {})
      })
    },
    [sendMessage]
  )

  const IntialiseMCQ = useCallback(
    (challengeId, attemptId) => {
      const payload = {
        attemptId,
        ...(challengeId != null && { challengeId })
      }
      sendMessage('challenge-mcq/load', payload)
    },
    [sendMessage]
  )

  const sendEditorFileOpen = useCallback(
    (filePath) => {
      sendMessage('file/open', { path: filePath })
    },
    [sendMessage]
  )

  const sendEditorFileChange = useCallback(
    (file) => {
      sendMessage('file/change', file)
    },
    [sendMessage]
  )

  const sendEditorFileDelete = useCallback(
    (files) => {
      files.forEach((file) => {
        sendMessage('file/delete', { path: file?.path })
      })
    },
    [sendMessage]
  )

  const sendEditorFileSave = useCallback(
    (files, dependencies, challengeType) => {
      return sendMessage(
        challengeType === CHALLENGE_TYPES.NOTEBOOK
          ? 'challenge-editor/notebook-save'
          : 'challenge-editor/save',
        {
          ...(challengeType === CHALLENGE_TYPES.NOTEBOOK
            ? { files }
            : {
                files: files.map((file) => ({
                  candidate: file?.candidate,
                  content: file?.content,
                  path: file?.path
                })),
                dependencies
              })
        }
      )
    },
    [sendMessage]
  )

  const sendSocketReconnect = useCallback(
    (
      programmingChallengeId,
      files,
      attemptId,
      contestId,
      dependencies = []
    ) => {
      sendMessage('challenge-editor/reconnect', {
        ...(contestId ? { contestId } : {}),
        ...(attemptId ? { attemptId } : {}),
        ...(dependencies?.length > 0 ? { dependencies } : {}),
        programmingChallengeId,
        files: files.map((file) => ({
          candidate: file?.candidate,
          content: file?.content,
          path: file?.path
        }))
      })
    },
    [sendMessage]
  )

  const sendEditorRunCode = useCallback(
    (files, dbtCommand) => {
      return sendMessage('challenge-editor/run', {
        files: files.map((file) => ({
          content: file?.content,
          path: file?.path
        })),
        ...(dbtCommand?.trim() ? { dbtCommand } : {})
      })
    },
    [sendMessage]
  )

  const sendCandidateDisqualify = useCallback(
    (candidateId, attemptId) => {
      sendMessage('contest/disqualify-candidate-attempt', {
        candidateId,
        attemptId
      })
    },
    [sendMessage]
  )

  const sendCandidateSubmitAttempt = useCallback(
    (candidateId, attemptId) => {
      sendMessage('challenge-editor/supervisor-submit', {
        candidateId,
        attemptId
      })
    },
    [sendMessage]
  )

  const sendCloseOtherConnection = useCallback(() => {
    sendMessage('challenge-editor/close-other-connections')
  }, [sendMessage])

  const sendSubmitContestEditor = useCallback(() => {
    sendMessage('challenge-editor/submit')
  }, [sendMessage])

  const sendEditorCodeForIntellisense = useCallback(
    (params) => {
      sendMessage('file/completion', params)
    },
    [sendMessage]
  )

  const sendInstallDependency = useCallback(
    (dependencies) => {
      sendMessage('language-server/install-dependencies', { dependencies })
    },
    [sendMessage]
  )

  const getMediaServerEndPoint = useCallback(
    (attemptId, stageId, excludeEndpoints = []) => {
      sendMessage('media-server/get-endpoint', {
        ...(attemptId ? { attemptId } : {}),
        ...(stageId ? { stageId } : {}),
        excludeEndpoints: [...excludeEndpoints]
      })
    },
    [sendMessage]
  )

  const sendDBLoad = useCallback(
    (
      databaseRuntime,
      databaseId,
      challengeId,
      attemptId,
      sql,
      courseId,
      topicPageId
    ) => {
      sendMessage('database/load', {
        ...(sql ? { sql } : {}),
        ...(courseId ? { courseId } : {}),
        ...(attemptId ? { attemptId } : {}),
        ...(databaseId ? { databaseId } : {}),
        ...(challengeId ? { challengeId } : {}),
        ...(topicPageId ? { topicPageId } : {}),
        ...(databaseRuntime ? { databaseRuntime } : {})
      })
    },
    [sendMessage]
  )

  const sendDBInit = useCallback(
    (sqlScript) => {
      sendMessage('database/init', {
        sqlScript
      })
    },
    [sendMessage]
  )

  const sendRunSQLScript = useCallback(
    (sqlScript, sql, candidateSelectedSQL) => {
      sendMessage('database/run-sql', {
        ...(sqlScript ? { sqlScript } : {}),
        ...(sql ? { sql } : {}),
        ...(candidateSelectedSQL ? { selectedSql: candidateSelectedSQL } : {})
      })
    },
    [sendMessage]
  )

  const sendSQLChange = useCallback(
    (sql) => {
      sendMessage('database/sql-change', {
        ...(sql ? { sql } : {})
      })
    },
    [sendMessage]
  )

  const sendRunAppSQL = useCallback(
    (sql) => {
      sendMessage('database/run-app-sql', {
        sql: sql || ''
      })
    },
    [sendMessage]
  )

  const sendResetAppDatabase = useCallback(() => {
    sendMessage('database/reset-app-database', {})
  }, [sendMessage])

  // AI Interview related handlers
  const InitAIScreening = useCallback(
    (attemptId, aiServerInstanceId = null) => {
      sendMessage('ai-screening/load', {
        attemptId,
        ...(aiServerInstanceId ? { aiServerInstanceId } : {})
      })
    },
    [sendMessage]
  )

  const sendGetAllMessages = useCallback(
    (attemptId, aiServerInstanceId) => {
      sendMessage('ai-screening/get-all-messages', {
        attemptId,
        aiServerInstanceId
      })
    },
    [sendMessage]
  )

  const sendEditorRestartApp = useCallback(() => {
    sendMessage('language-server/restart-app')
  }, [sendMessage])

  //useEffect
  useEffect(() => {
    return () => {
      closeSocket(true)
    }
  }, [closeSocket])

  return (
    <SocketContext.Provider
      value={{
        socket: socket.current,
        initSocket,
        sendDBLoad,
        sendDBInit,
        closeSocket,
        IntialiseMCQ,
        sendRunAppSQL,
        sendSQLChange,
        IntialiseEditor,
        sendRunSQLScript,
        isSocketConnected,
        sendEditorRunCode,
        sendEditorFileOpen,
        sendEditorFileSave,
        sendEditorRestartApp,
        SendSocketHeartBeat,
        socketAuthenticated,
        sendSocketReconnect,
        sendResetAppDatabase,
        sendEditorFileDelete,
        sendEditorFileChange,
        sendInstallDependency,
        setSocketAuthenticated,
        getMediaServerEndPoint,
        sendSubmitContestEditor,
        sendCandidateDisqualify,
        sendCloseOtherConnection,
        sendCandidateSubmitAttempt,
        sendEditorCodeForIntellisense,
        handleSocketAuthorization,
        InitAIScreening,
        sendGetAllMessages
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}
